/**
 * Low-level HTTP/stream helpers shared by every provider. Zero dependencies —
 * built on the browser `fetch` + `ReadableStream` primitives.
 */

export class ProviderHttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ProviderHttpError";
  }
}

/** Throws a descriptive error if the response is not ok, reading the body. */
export async function assertOk(res: Response): Promise<Response> {
  if (res.ok) return res;
  let detail = "";
  try {
    const text = await res.text();
    try {
      const json = JSON.parse(text);
      detail = json?.error?.message ?? json?.message ?? text;
    } catch {
      detail = text;
    }
  } catch {
    /* ignore body read failures */
  }
  throw new ProviderHttpError(
    res.status,
    `HTTP ${res.status} ${res.statusText}${detail ? ` — ${detail}` : ""}`,
  );
}

/**
 * Yields the `data:` payload of each SSE event block (Anthropic, OpenAI).
 * Multi-line data blocks are joined with "\n". Comment/heartbeat lines are
 * skipped.
 */
export async function* readSSE(res: Response): AsyncGenerator<string> {
  if (!res.body) throw new Error("Response has no readable body");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const data = block
          .split("\n")
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).replace(/^ /, ""))
          .join("\n");
        if (data) yield data;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/** Yields each newline-delimited JSON object (Ollama streaming). */
export async function* readJsonLines(res: Response): AsyncGenerator<unknown> {
  if (!res.body) throw new Error("Response has no readable body");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let nl: number;
      while ((nl = buffer.indexOf("\n")) !== -1) {
        const line = buffer.slice(0, nl).trim();
        buffer = buffer.slice(nl + 1);
        if (line) yield JSON.parse(line);
      }
    }
    const tail = buffer.trim();
    if (tail) yield JSON.parse(tail);
  } finally {
    reader.releaseLock();
  }
}

/** Parses a JSON string; returns `{}` on failure so a bad arg never throws. */
export function safeJson(text: string): Record<string, unknown> {
  if (!text) return {};
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}
